import { type DataLayerWindow } from './core';
import { initializeInteractionTracking } from './interactions';
import { trackPageLoad } from './page-load';

type FeatureTracker = {
  disconnect: () => void;
  evaluate: () => unknown;
};

const ECOMMERCE_SURFACE_SELECTORS = [
  '.product-hero',
  '.related-products-block',
  '.solution-area-products__area',
  '.sub-industry-card-list__card',
  '.product-listing-all',
  '.features-comparison-table',
  '.product-card'
] as const;

function looksLikeBlogArticlePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  const blogIndex = segments.findIndex(segment => segment.toLocaleLowerCase() === 'blog');

  return blogIndex >= 0 && segments.length === blogIndex + 2;
}

function hasSelector(targetDocument: Document, selector: string): boolean {
  return (
    typeof targetDocument.querySelector === 'function' &&
    Boolean(targetDocument.querySelector(selector))
  );
}

function shouldLoadBlogTracking(targetWindow: DataLayerWindow, targetDocument: Document): boolean {
  if (hasSelector(targetDocument, '[data-component="news-article"]')) return true;

  return (
    looksLikeBlogArticlePath(targetWindow.location?.pathname ?? '') &&
    hasSelector(targetDocument, 'main.visual-builder-experience')
  );
}

function shouldLoadEcommerceTracking(targetDocument: Document): boolean {
  return ECOMMERCE_SURFACE_SELECTORS.some(selector => hasSelector(targetDocument, selector));
}

function shouldLoadPromotionTracking(targetDocument: Document): boolean {
  return hasSelector(
    targetDocument,
    '[data-promo-card], .promotion-banner, .pricing-option-card__badge--promotion'
  );
}

export function initializeDataLayerTracking(
  targetWindow: DataLayerWindow = window,
  targetDocument: Document = document
): () => void {
  // The production entry can be evaluated under more than one URL (for example, with and
  // without a cache-busting query string). Keep the singleton state on window so separately
  // evaluated module instances cannot register duplicate analytics tracking.
  if (targetWindow.__bentleyDataLayerTrackingInitialized) return () => undefined;
  targetWindow.__bentleyDataLayerTrackingInitialized = true;

  let blogTracker: FeatureTracker | undefined;
  let ecommerceTracker: FeatureTracker | undefined;
  let blogImport: Promise<void> | undefined;
  let ecommerceImport: Promise<void> | undefined;
  let promotionImport: Promise<void> | undefined;
  let stopPromotionTracking: (() => void) | undefined;
  const stopInteractionTracking = initializeInteractionTracking(targetWindow, targetDocument);

  const loadFeatureTrackers = (): Promise<void> => {
    const pending: Promise<void>[] = [];

    if (!blogTracker && !blogImport && shouldLoadBlogTracking(targetWindow, targetDocument)) {
      blogImport = import('./blog').then(module => {
        blogTracker = module.initializeBlogDetailsTracking(targetWindow, targetDocument);
        blogTracker.evaluate();
      });
      pending.push(blogImport);
    }

    if (!ecommerceTracker && !ecommerceImport && shouldLoadEcommerceTracking(targetDocument)) {
      ecommerceImport = import('./ecommerce').then(module => {
        ecommerceTracker = module.initializeEcommerceTracking(targetWindow, targetDocument);
        if (targetDocument.body?.dataset.modulesLoaded === 'true') ecommerceTracker.evaluate();
      });
      pending.push(ecommerceImport);
    }

    if (!stopPromotionTracking && !promotionImport && shouldLoadPromotionTracking(targetDocument)) {
      promotionImport = import('./promotions').then(module => {
        stopPromotionTracking = module.initializePromotionTracking(targetWindow, targetDocument);
      });
      pending.push(promotionImport);
    }

    return Promise.all(pending).then(() => undefined);
  };

  const fireLoadedEvents = (): Promise<void> => {
    trackPageLoad(targetWindow, targetDocument);
    return loadFeatureTrackers();
  };

  if (targetDocument.readyState === 'complete') {
    targetWindow.setTimeout(fireLoadedEvents, 0);
  } else {
    targetWindow.addEventListener('load', fireLoadedEvents, { once: true });
  }

  let presenceObserver: MutationObserver | undefined;
  if (targetDocument.body && typeof MutationObserver === 'function') {
    presenceObserver = new MutationObserver(() => {
      void loadFeatureTrackers();
    });
    presenceObserver.observe(targetDocument.body, { childList: true, subtree: true });
  }

  return () => {
    targetWindow.removeEventListener('load', fireLoadedEvents);
    presenceObserver?.disconnect();
    blogTracker?.disconnect();
    ecommerceTracker?.disconnect();
    stopInteractionTracking();
    stopPromotionTracking?.();
    delete targetWindow.__bentleyDataLayerTrackingInitialized;
  };
}
